import { z } from "zod";

// ---------------------------------------------------------------------------
// take-home — the net-pay PRODUCT surface.
//
// The panel (panels/take-home-pay.md) made this binding: take-home-pay ships
// NOT as a bare net-pay page but as the suite's shared net-pay engine + traffic
// anchor whose DIFFERENTIATED MODES are the marketable surfaces. This module is
// those modes. It owns NO tax arithmetic of its own — every figure routes
// through the FY-versioned `income-tax` engine (../income-tax) so the marginal
// brackets, Medicare shade-in, LITO taper and the new marginal HELP/STSL bands
// stay in one cited, unit-tested place. Each surface here is a thin, pure
// function: inputs -> income-tax engine -> a focused output. No I/O, no
// Date.now(); the assessment FY is always a parameter.
//
// Regulatory posture (panels lens 2 — MEDIUM, the only live risk): every output
// is an ESTIMATE, framed as information not advice. No "save $X", no "you
// should", no product/credit handoff. The mode that flirts with advice
// (pay-rise framing) states numbers only. A hard-rule guard (`assertNoHandoff`
// in calculate.ts) keeps any financial-product / credit identifier out of a
// personalised result, tested in calculate.test.ts.
// ---------------------------------------------------------------------------

/**
 * Common shape for the salary-side inputs every mode shares. `assessmentYear`
 * selects the FY ruleset (never derived from the clock). `hasStudyLoan` gates
 * the HELP/STSL line; `residency` and `household` thread through to the engine.
 */
export const TakeHomeInputSchema = z.object({
  /** Gross taxable income for the FY (assessable income minus deductions). */
  grossIncome: z.number().min(0),
  /** Residency status for the FY. Defaults to resident. */
  residency: z.enum(["resident", "foreign", "whm"]).default("resident"),
  /** Has an outstanding HELP/STSL loan — gates the compulsory repayment line. */
  hasStudyLoan: z.boolean().default(false),
  /** Family context for the Medicare-levy low-income reduction. */
  family: z.boolean().default(false),
  /** Dependent children (per-child Medicare threshold uplift). */
  dependants: z.number().int().min(0).default(0),
  /** The financial year being assessed, e.g. "2025-26". */
  assessmentYear: z.string(),
});
export type TakeHomeInput = z.input<typeof TakeHomeInputSchema>;
export type TakeHomeParsed = z.infer<typeof TakeHomeInputSchema>;

/** Net-pay breakdown — what `takeHome` returns. All whole dollars. */
export interface TakeHomeOutput {
  /** Gross taxable income the calc ran against. */
  grossIncome: number;
  /** Gross income tax before offsets/levy. */
  incomeTax: number;
  /** Medicare levy after any low-income reduction. */
  medicareLevy: number;
  /** LITO applied (resident-only, capped). */
  lito: number;
  /** Net tax = max(0, incomeTax − offsets) + medicareLevy. */
  netTax: number;
  /** Compulsory HELP/STSL repayment (0 if no loan or below threshold). */
  helpRepayment: number;
  /** Take-home = grossIncome − netTax − helpRepayment. */
  takeHome: number;
  /** Average rate of everything withheld vs gross (netTax + HELP) / gross. */
  averageRate: number;
}

/**
 * Marginal keep-rate analysis — what a pay rise ACTUALLY adds. Splits the next
 * raise into the four wedges (income tax, Medicare, LITO claw-back, marginal
 * HELP step) so the user sees the true keep-rate, not the headline bracket.
 */
export const PayRiseInputSchema = TakeHomeInputSchema.extend({
  /** The gross pay rise being assessed (e.g. $10,000). */
  riseAmount: z.number().min(0),
});
export type PayRiseInput = z.input<typeof PayRiseInputSchema>;
export type PayRiseParsed = z.infer<typeof PayRiseInputSchema>;

export interface PayRiseOutput {
  /** Gross income before the rise. */
  grossIncome: number;
  /** The gross rise assessed. */
  riseAmount: number;
  /** Extra income tax on the rise. */
  extraIncomeTax: number;
  /** Extra Medicare levy on the rise. */
  extraMedicareLevy: number;
  /** Extra LITO claw-back caused by the rise (the taper, as a positive cost). */
  litoClawback: number;
  /** Extra compulsory HELP/STSL repayment caused by the rise (0 if no loan). */
  extraHelpRepayment: number;
  /** Total withheld from the rise = tax + Medicare + LITO claw-back + HELP. */
  totalDeductions: number;
  /** Net amount kept from the rise = riseAmount − totalDeductions. */
  keptAmount: number;
  /** Keep-rate on the rise = keptAmount / riseAmount (fraction). */
  keepRate: number;
  /** Effective marginal rate on the rise = 1 − keepRate (fraction). */
  effectiveMarginalRate: number;
}

/**
 * Silent-bill warning for an undeclared HELP/STSL debt. STSL withholding is
 * opt-in: if the employee never told the payer (TFN decl NAT 3092 Q10 / NAT
 * 3093 Q6 = "No"), the payer withholds nothing for the loan, and the whole
 * compulsory repayment lands as a lump sum at lodgement.
 */
export const HelpWarningInputSchema = TakeHomeInputSchema.extend({
  /**
   * Whether the employer withholds STSL amounts (the TFN/withholding
   * declaration question is "Yes"). When false, nothing is withheld for the
   * loan all year and the whole repayment is the predicted lump-sum bill.
   */
  employerWithholdsStsl: z.boolean().default(false),
}).extend({
  /** Force the loan flag on — a HELP warning only makes sense with a loan. */
  hasStudyLoan: z.literal(true).default(true),
});
export type HelpWarningInput = z.input<typeof HelpWarningInputSchema>;
export type HelpWarningParsed = z.infer<typeof HelpWarningInputSchema>;

export interface HelpWarningOutput {
  /** Repayment income the band math used. */
  repaymentIncome: number;
  /** Full compulsory HELP/STSL repayment for the FY. */
  compulsoryRepayment: number;
  /** Whether the employer is withholding for the loan. */
  employerWithholdsStsl: boolean;
  /**
   * Predicted lump-sum bill at lodgement attributable to the loan: the full
   * repayment when the employer withholds nothing, else $0 (withheld through
   * the year). Information only — the ATO finalises the actual assessment.
   */
  predictedLumpSumBill: number;
  /** True when a silent bill is predicted (loan present, nothing withheld). */
  silentBill: boolean;
}

/**
 * Withheld-vs-liability reconciliation. The user supplies what was actually
 * withheld through the year (from payslips/income statement); we compute the
 * annual liability from the engine and predict the refund (over-withheld) or
 * bill (under-withheld).
 */
export const WithholdingGapInputSchema = TakeHomeInputSchema.extend({
  /** Total PAYG tax + Medicare withheld through the year (excl. STSL). */
  taxWithheld: z.number().min(0),
  /** Total STSL/HELP amounts withheld through the year. Default 0. */
  stslWithheld: z.number().min(0).default(0),
});
export type WithholdingGapInput = z.input<typeof WithholdingGapInputSchema>;
export type WithholdingGapParsed = z.infer<typeof WithholdingGapInputSchema>;

export interface WithholdingGapOutput {
  /** Annual net tax (income tax less offsets + Medicare levy). */
  annualNetTax: number;
  /** Annual compulsory HELP/STSL repayment (0 if no loan). */
  annualHelpRepayment: number;
  /** Total annual liability = annualNetTax + annualHelpRepayment. */
  annualLiability: number;
  /** Total withheld = taxWithheld + stslWithheld. */
  totalWithheld: number;
  /**
   * Signed gap = totalWithheld − annualLiability. Positive ⇒ predicted refund;
   * negative ⇒ predicted bill. Information only — the ATO finalises.
   */
  gap: number;
  /** Predicted refund (gap when positive, else 0). */
  predictedRefund: number;
  /** Predicted bill (−gap when negative, else 0). */
  predictedBill: number;
  /** "refund" | "bill" | "balanced". */
  outcome: "refund" | "bill" | "balanced";
}

/**
 * Second-job under-withholding from claiming the tax-free threshold on the
 * wrong payer. The ATO rule: claim the threshold from only ONE payer (usually
 * the highest). When claimed on both, the second payer treats the second job's
 * first $18,200 as tax-free even though the threshold is already consumed by
 * job 1 — so that slice should have been taxed at the user's real marginal rate.
 */
export const SecondJobInputSchema = z.object({
  /** Gross income from the primary (highest-paying) job. */
  primaryJobIncome: z.number().min(0),
  /** Gross income from the second job. */
  secondJobIncome: z.number().min(0),
  /**
   * Whether the tax-free threshold is being claimed on the SECOND job too (the
   * trap). When false, no double-count and no predicted shortfall from it.
   */
  claimsThresholdOnSecondJob: z.boolean().default(true),
  /** Residency for the FY. Defaults to resident. */
  residency: z.enum(["resident", "foreign", "whm"]).default("resident"),
  /** The financial year being assessed, e.g. "2025-26". */
  assessmentYear: z.string(),
});
export type SecondJobInput = z.input<typeof SecondJobInputSchema>;
export type SecondJobParsed = z.infer<typeof SecondJobInputSchema>;

export interface SecondJobOutput {
  /** Combined income across both jobs. */
  combinedIncome: number;
  /** Whether the threshold is double-claimed (the trap is active). */
  claimsThresholdOnSecondJob: boolean;
  /**
   * The slice of income wrongly treated as tax-free by the second payer — the
   * tax-free threshold when double-claimed (capped at the second job's income),
   * else 0.
   */
  doubleCountedThreshold: number;
  /** The user's true marginal rate on that slice (from the engine). */
  marginalRate: number;
  /**
   * Predicted under-withholding ≈ doubleCountedThreshold × marginalRate. The
   * approximate end-of-year shortfall the trap creates. Information only — the
   * ATO finalises the actual assessment.
   */
  predictedShortfall: number;
}
